import type { NlRcphSegment, NlRcphSubSegment } from './nl-rcph-activity-configuration.ts'
import {
  getNlRcphArchiPiDeductible,
  NL_RCPH_ARCHI_CIVIL_PI_DEDUCTIBLE,
  NL_RCPH_ARCHI_INTERIOR_PI_DEDUCTIBLE,
  type NlRcphResolvedActivity,
} from './nl-rcph-archi-activities.ts'
import { isNlRcphExecutorActivity, NL_RCPH_EXECUTOR_PI_LIMITS } from './nl-rcph-executor-activities.ts'

/**
 * PI limit, annual limit and deductible options per segment.
 */
export type NlRcphSegmentLimitConfiguration = {
  readonly piLimitsPerClaim: readonly number[]
  readonly piAnnualLimits: readonly number[]
  readonly piDeductibles: readonly number[]
}

export const nlRcphSegmentLimitConfiguration = {
  ARCHI: {
    piLimitsPerClaim: [1_000_000, 2_500_000],
    piAnnualLimits: [2_000_000, 5_000_000],
    piDeductibles: [NL_RCPH_ARCHI_CIVIL_PI_DEDUCTIBLE],
  },
  MAC: {
    piLimitsPerClaim: [100_000, 250_000, 500_000, 1_000_000, 1_250_000],
    piAnnualLimits: [100_000, 200_000, 250_000, 500_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000],
    piDeductibles: [0, 1_000],
  },
  MAN: {
    piLimitsPerClaim: [100_000, 250_000, 500_000, 1_000_000, 1_250_000],
    piAnnualLimits: [100_000, 200_000, 250_000, 500_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000],
    piDeductibles: [0, 1_000],
  },
  MISC_ACCOUNTANTS: {
    piLimitsPerClaim: [500_000, 1_000_000],
    piAnnualLimits: [500_000, 600_000, 1_000_000, 2_000_000],
    piDeductibles: [1_000],
  },
  MISC_GENERAL: {
    piLimitsPerClaim: [100_000, 250_000, 500_000, 1_000_000, 1_250_000],
    piAnnualLimits: [100_000, 200_000, 250_000, 500_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000],
    piDeductibles: [0, 1_000],
  },
  MISC_LEGAL: {
    piLimitsPerClaim: [250_000, 500_000, 1_000_000],
    piAnnualLimits: [100_000, 200_000, 250_000, 500_000, 1_000_000, 2_000_000],
    piDeductibles: [1_000],
  },
  SECU: {
    piLimitsPerClaim: [250_000, 500_000, 1_000_000],
    piAnnualLimits: [500_000, 1_000_000, 2_000_000],
    piDeductibles: [1_000],
  },
  TECH: {
    piLimitsPerClaim: [100_000, 250_000, 500_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000],
    piAnnualLimits: [
      100_000, 200_000, 250_000, 500_000, 1_000_000, 1_250_000, 1_500_000, 2_000_000, 2_500_000, 3_000_000, 4_000_000,
      5_000_000,
    ],
    piDeductibles: [0, 1_000],
  },
} as const satisfies Record<NlRcphSegment, NlRcphSegmentLimitConfiguration>

// Mirrors nlRcphPiBySubSegment in the pricing package: each sub-segment has its own limits.
// The segment-level entries above keep the most conservative values as the fallback when no
// sub-segment is known.
export const nlRcphLimitConfigurationBySubSegment = {
  Administratiekantoor: {
    piLimitsPerClaim: [250_000, 500_000, 1_000_000],
    piAnnualLimits: [250_000, 500_000, 1_000_000, 2_000_000],
    piDeductibles: [1_000],
  },
  Accountantskantoor: {
    piLimitsPerClaim: [300_000, 500_000, 1_000_000],
    piAnnualLimits: [300_000, 500_000, 600_000, 1_000_000, 2_000_000],
    piDeductibles: [1_000],
  },
  'Bouwkundig teken- en adviesbureau': {
    piLimitsPerClaim: [1_000_000, 2_500_000],
    piAnnualLimits: [2_000_000, 5_000_000],
    piDeductibles: [NL_RCPH_ARCHI_CIVIL_PI_DEDUCTIBLE],
  },
  Architectenbureau: {
    piLimitsPerClaim: [1_000_000, 2_500_000],
    piAnnualLimits: [2_000_000, 5_000_000],
    piDeductibles: [NL_RCPH_ARCHI_CIVIL_PI_DEDUCTIBLE],
  },
  Interieurarchitect: {
    piLimitsPerClaim: [1_000_000, 2_500_000],
    piAnnualLimits: [2_000_000, 5_000_000],
    piDeductibles: [NL_RCPH_ARCHI_INTERIOR_PI_DEDUCTIBLE],
  },
} as const satisfies Record<NlRcphSubSegment, NlRcphSegmentLimitConfiguration>

function uniqueSorted(values: readonly number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b)
}

function resolveNlRcphLimitConfiguration(
  segment: NlRcphSegment,
  subSegment: NlRcphSubSegment | undefined,
): NlRcphSegmentLimitConfiguration {
  if (subSegment) {
    return nlRcphLimitConfigurationBySubSegment[subSegment]
  }
  return nlRcphSegmentLimitConfiguration[segment]
}

export function getNlRcphAllowedPiLimitsForSegment(
  segment: NlRcphSegment,
  subSegment?: NlRcphSubSegment,
): NlRcphLimit[] {
  return [...resolveNlRcphLimitConfiguration(segment, subSegment).piLimitsPerClaim]
}

export function getNlRcphAllowedPiLimits(
  segment: NlRcphSegment,
  activities: readonly NlRcphResolvedActivity[],
  subSegment?: NlRcphSubSegment,
): NlRcphLimit[] {
  if (segment === 'MISC_GENERAL' && isNlRcphExecutorActivity(activities)) {
    return [...NL_RCPH_EXECUTOR_PI_LIMITS]
  }

  return getNlRcphAllowedPiLimitsForSegment(segment, resolveNlRcphAccountantSubSegment(activities, subSegment))
}

export function getNlRcphAllowedPiAnnualLimitsForSegment(
  segment: NlRcphSegment,
  subSegment?: NlRcphSubSegment,
): NlRcphDoubleLimit[] {
  return [...resolveNlRcphLimitConfiguration(segment, subSegment).piAnnualLimits]
}

function resolveNlRcphAccountantSubSegment(
  activities: readonly NlRcphResolvedActivity[],
  subSegment: NlRcphSubSegment | undefined,
): NlRcphSubSegment | undefined {
  const hasBothAccountantSubSegments =
    activities.some(({ subSegment: activitySubSegment }) => activitySubSegment === 'Administratiekantoor') &&
    activities.some(({ subSegment: activitySubSegment }) => activitySubSegment === 'Accountantskantoor')
  if (hasBothAccountantSubSegments) return 'Accountantskantoor'
  return subSegment ?? activities[0]?.subSegment
}

export function getNlRcphAllowedPiDeductiblesForSegment(
  segment: NlRcphSegment,
  subSegment?: NlRcphSubSegment,
): NlRcphDeductible[] {
  return [...resolveNlRcphLimitConfiguration(segment, subSegment).piDeductibles]
}

export function getNlRcphAllowedPiDeductibles(
  segment: NlRcphSegment,
  activities: readonly NlRcphResolvedActivity[],
  subSegment?: NlRcphSubSegment,
): NlRcphDeductible[] {
  if (segment === 'ARCHI') {
    return [getNlRcphArchiPiDeductible(activities)]
  }

  return getNlRcphAllowedPiDeductiblesForSegment(segment, resolveNlRcphAccountantSubSegment(activities, subSegment))
}

export function getNlRcphDoubledAnnualLimit(perClaimLimit: number): number | undefined {
  const doubledLimit = perClaimLimit * 2
  return doubledLimit
}

function getAllNlRcphLimitConfigurations(): NlRcphSegmentLimitConfiguration[] {
  return [...Object.values(nlRcphSegmentLimitConfiguration), ...Object.values(nlRcphLimitConfigurationBySubSegment)]
}

export const NL_RCPH_LIMITS = uniqueSorted(
  getAllNlRcphLimitConfigurations().flatMap((configuration) => configuration.piLimitsPerClaim),
)

export const NL_RCPH_DOUBLE_LIMITS = uniqueSorted(
  getAllNlRcphLimitConfigurations().flatMap((configuration) => configuration.piAnnualLimits),
)

export const NL_RCPH_DEDUCTIBLES = uniqueSorted(
  getAllNlRcphLimitConfigurations().flatMap((configuration) => configuration.piDeductibles),
)

export type NlRcphLimit = (typeof NL_RCPH_LIMITS)[number]
export type NlRcphDoubleLimit = (typeof NL_RCPH_DOUBLE_LIMITS)[number]
export type NlRcphDeductible = (typeof NL_RCPH_DEDUCTIBLES)[number]
